import type {
  bonusReportEnabledFilters,
  codeReportEnabledFilters,
  offerReportEnabledFilters,
} from '@features/filters/chipBasedFilters/constants';
import type { ValidationSchema as ChipBasedValidationSchema } from '@features/filters/chipBasedFilters/model/validation.model';
import type {
  activityReportEnabledFilters,
  automaticCampaignEnabledFilters,
  bonusesPendingReviewEnabledFilters,
  bonusManagementEnabledFilters,
  codeCampaignEnabledFilters,
  contributionBetsReportEnabledFilters,
  monitorReportEnabledFilters,
  scheduledCampaignEnabledFilters,
} from '@features/filters/fieldBasedFilters/constants';
import type { ValidationSchema as FieldBasedValidationSchema } from '@features/filters/fieldBasedFilters/model/validation.model';
import type { PLAYER_BONUSES_QUERY_PARAM } from '@modules/playerBonuses/submodules/TableFilters/constants';
import type { PLAYER_CODES_QUERY_PARAM } from '@modules/playerCodes/submodules/TableFilters/constants';
import type { PLAYER_OFFERS_QUERY_PARAM } from '@modules/playerOffers/submodules/TableFilters/constants';
import type { IOption } from '@shared/form/types';
import type { RequiredStrict } from '@types';
import type { Dayjs } from 'dayjs';

import type { InputProps, StackProps, TextFieldProps } from '@mui/material';

type FilterValue =
  | null
  | Dayjs
  | number
  | string
  | IOption
  | boolean
  | number[]
  | string[]
  | IOption[];

export type FilterFormData = Record<
  string,
  Partial<{
    operator?: string;
    inversion: boolean;
    value: FilterValue;
    fieldLabel?: string;
  }>
>;

export interface IFiltersConfig {
  label: string;
  order: number;
  enabled: boolean;
  fieldName: string;
  disabled?: boolean;
  limitTags?: number;
  multiple?: boolean;
  options?: IOption[];
  defaultValue?: string;
  isNumberInput?: boolean;
  operator: OPERATOR_TYPE;
  inputProps?: InputProps;
  formElement: FORM_ELEMENT;
  containerProps?: StackProps;
  isFloatNumberInput?: boolean;
  withVirtualization?: boolean;
  possibleOperators: OPERATOR_TYPE[];
  sx?: Exclude<TextFieldProps['sx'], undefined>;
  sxControl?: Exclude<TextFieldProps['sx'], undefined>;
}

export enum FORM_ELEMENT {
  AUTOCOMPLETE = 'AUTOCOMPLETE',
  CHECKBOX = 'CHECKBOX',
  DATE = 'DATE',
  DATE_RANGE_END = 'DATE_RANGE_END',
  DATE_RANGE_START = 'DATE_RANGE_START',
  DATE_TIME = 'DATE_TIME',
  INPUT = 'INPUT',
  SELECT = 'SELECT',
}

export enum OPERATOR_TYPE {
  EQUAL = 'EQUAL',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_THAN_OR_EQUAL = 'GREATER_THAN_OR_EQUAL',
  IN = 'IN',
  LESS_THAN = 'LESS_THAN',
  LESS_THAN_OR_EQUAL = 'LESS_THAN_OR_EQUAL',
  LIKE = 'LIKE',
  NOT_EQUAL = 'NOT_EQUAL',
  NOT_IN = 'NOT_IN',
  NOT_LIKE = 'NOT_LIKE',
}

export interface ISearchParameters {
  page: number;
  size: number;
  sorts: ISortParameters[];
  filters: IFiltersParameters[];
  isIncludeExternalBonuses?: boolean;
}

export interface IInitialFilter {
  operator?: string;
  inversion?: boolean;
  value?: FilterValue;
}

export interface ISortParameters {
  field: string;
  order: OPERATOR_SORT;
}
export interface IFiltersParameters {
  field: string;
  value: FilterValue;
  inversion?: boolean;
  type: OPERATOR_TYPE;
}

export enum OPERATOR_SORT {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface IFilterChip {
  field: string;
  operator?: string;
  inversion?: boolean;
  value?: FilterValue;
  fieldLabel?: string;
  formElement?: string;
  valueLabel?: number | string | string[];
}

export enum CHIPS_ALLOWED_MODULE {
  BONUSES = 'playerBonuses',
  CODE = 'playerCode',
  OFFERS = 'playerOffers',
}

export interface IModuleQueryParametersMap {
  [CHIPS_ALLOWED_MODULE.CODE]: typeof PLAYER_CODES_QUERY_PARAM;
  [CHIPS_ALLOWED_MODULE.OFFERS]: typeof PLAYER_OFFERS_QUERY_PARAM;
  [CHIPS_ALLOWED_MODULE.BONUSES]: typeof PLAYER_BONUSES_QUERY_PARAM;
}

export enum FIELD_FILTERS_ALLOWED_MODULE {
  AUTOMATIC_CAMPAIGN = 'automaticCampaign',
  BONUS = 'bonus',
  BONUSES_PENDING_REVIEW = 'bonusesPendingReview',
  CAMPAIGN_ACTIVITY = 'campaignActivity',
  CAMPAIGN_MONITOR = 'campaignMonitor',
  CODE_CAMPAIGN = 'codeCampaign',
  CONTRIBUTION_BETS_REPORT = 'contributionBetsReport',
  SCHEDULED_CAMPAIGN = 'scheduledCampaign',
}

export enum CHIP_FILTERS_ENTITY {
  ACCEPTED_AT = 'acceptedAt-At',
  ACCEPTED_TO = 'acceptedAt-To',
  ACTIVATED_AT = 'activatedAt-At',
  ACTIVATED_TO = 'activatedAt-To',
  APPLIED_AT = 'appliedAt-At',
  APPLIED_BY_ID = 'appliedById',
  APPLIED_TO = 'appliedAt-To',
  BET_REFERENCE = 'betReference',
  BONUS_TEMPLATE_ID = 'bonusTemplateId',
  BRAND_ID = 'brandId',
  CAMPAIGN_ID = 'campaignId',
  CANCELLED_AT = 'cancelledAt-At',
  CANCELLED_BY = 'cancelledBy',
  CANCELLED_BY_ID = 'cancelledById',
  CANCELLED_TO = 'cancelledAt-To',
  CATEGORY = 'categoryId',
  CODE = 'code',
  CODE_ID = 'campaignCodeActivationId',
  CONVERTED_AMOUNT_AT = 'convertedAmount-At',
  CONVERTED_AMOUNT_SYSTEM_CURRENCY_AT = 'convertedAmountSystemCurrency-At',
  CONVERTED_AMOUNT_SYSTEM_CURRENCY_TO = 'convertedAmountSystemCurrency-To',
  CONVERTED_AMOUNT_TO = 'convertedAmount-To',
  CONVERTED_AT = 'convertedAt-At',
  CONVERTED_TO = 'convertedAt-To',
  COUNTRY_ID = 'countryId',
  CURRENCY = 'currencyId',
  DECLINED_AT = 'declinedAt-At',
  DECLINED_TO = 'declinedAt-To',
  EXPIRES_AT = 'expiresAt-At',
  EXPIRES_TO = 'expiresAt-To',
  FILTER_TYPE = 'filterType',
  GIVEN_AMOUNT_AT = 'givenAmount-At',
  GIVEN_AMOUNT_SYSTEM_CURRENCY_AT = 'givenAmountSystemCurrency-At',
  GIVEN_AMOUNT_SYSTEM_CURRENCY_TO = 'givenAmountSystemCurrency-To',
  GIVEN_AMOUNT_TO = 'givenAmount-To',
  GIVEN_AT = 'givenAt-At',
  GIVEN_TO = 'givenAt-To',
  ID = 'id',
  ISSUED_BY = 'issuedBy',
  ISSUED_BY_ID = 'issuedById',
  ISSUING_METHOD = 'issuingMethod',
  OFFER_ID = 'offerId',
  PAYOUT_TYPE = 'payoutType',
  PLAYER_ID = 'playerId',
  PROFIT_BOOST_PERCENTAGE = 'profitBoostPercentage',
  STATUS = 'status',
  TRANSACTION_ID = 'transactionId',
  TYPE = 'type',
}

export enum FIELD_FILTERS_ENTITY {
  ACTIVATION_ACTION = 'activationAction',
  BET_ACTION_TYPE = 'betActionType',
  BET_ID = 'betId',
  BONUS_TEMPLATE_ID = 'bonusTemplateId',
  BONUSES = 'bonuses',
  BRAND_ID = 'brandId',
  BRANDS = 'brands',
  CAMPAIGN_END_DATE = 'endDate',
  CAMPAIGN_EXECUTION_ID = 'campaignExecutionId',
  CAMPAIGN_ID = 'campaignId',
  CAMPAIGN_IDS = 'campaignIds',
  CAMPAIGN_NAME = 'campaignName',
  CAMPAIGN_START_DATE = 'startDate',
  CAMPAIGN_TYPE = 'boCampaignType',
  CATEGORY = 'categoryId',
  CODE = 'code',
  CONTRIBUTION_CREATED_AT_FROM = 'createdAt-At',
  CONTRIBUTION_CREATED_AT_TO = 'createdAt-To',
  CONTRIBUTION_REPORT_BET_REFERENCE = 'betReference',
  CONTRIBUTION_REPORT_GAME_ID = 'gameId',
  CONTRIBUTION_REPORT_GAME_SUB_PROVIDER_ID = 'gameSubProviderId',
  CONTRIBUTION_REPORT_GAME_TYPE_ID = 'gameTypeId',
  DECISION = 'decision',
  ENABLED = 'enabled',
  EVENT_ID = 'triggerId',
  FAILED_CHECK = 'failedCheck',
  GAME_NAME = 'gameName',
  GAME_SUB_PROVIDER = 'gameSubProvider',
  GAME_TYPE = 'gameType',
  GIVEN_AT = 'givenAt-At',
  GIVEN_TO = 'givenAt-To',
  HIDDEN = 'hidden',
  ID = 'id',
  NAME = 'name',
  OCCURRENCE_DATE_FROM = 'occurrenceDate-From',
  OCCURRENCE_DATE_TO = 'occurrenceDate-To',
  OFFER_STATUS = 'offerStatus',
  PLAYER_ID = 'playerId',
  PRIMARY_CRITERION = 'primaryCriterion',
  SHOW_ADVANCED_FILTERS = 'showAdvancedFilters',
  SHOW_ONLY_PENDING_REVIEWS = 'showOnlyPendingReviews',
  TARGET_GROUP = 'targetGroup',
  TRIGGER = 'trigger',
  TRIGGER_DATE_FROM = 'triggerReceivedAt-At',
  TRIGGER_DATE_TO = 'triggerReceivedAt-To',
  TRIGGER_TYPE = 'triggerType',
  TYPE = 'type',
}

export type AutomaticCampaignFiltersValidationSchema = RequiredStrict<
  FieldBasedValidationSchema<typeof automaticCampaignEnabledFilters>
>;

export type ContributionBetsReportFiltersValidationSchema = RequiredStrict<
  FieldBasedValidationSchema<typeof contributionBetsReportEnabledFilters>
>;

export type ScheduledCampaignFiltersValidationSchema = RequiredStrict<
  FieldBasedValidationSchema<typeof scheduledCampaignEnabledFilters>
>;

export type CodeCampaignFiltersValidationSchema = RequiredStrict<
  FieldBasedValidationSchema<typeof codeCampaignEnabledFilters>
>;

export type ActivityReportFiltersValidationSchema = RequiredStrict<
  FieldBasedValidationSchema<typeof activityReportEnabledFilters>
>;

export type MonitorReportFiltersValidationSchema = RequiredStrict<
  FieldBasedValidationSchema<typeof monitorReportEnabledFilters>
>;

export type BonusManagementFiltersValidationSchema = RequiredStrict<
  FieldBasedValidationSchema<typeof bonusManagementEnabledFilters>
>;

export type BonusesPendingReviewReportFiltersValidationSchema = RequiredStrict<
  FieldBasedValidationSchema<typeof bonusesPendingReviewEnabledFilters>
>;

export type OfferReportFiltersValidationSchema = RequiredStrict<
  ChipBasedValidationSchema<typeof offerReportEnabledFilters>
>;

export type CodeReportFiltersValidationSchema = RequiredStrict<
  ChipBasedValidationSchema<typeof codeReportEnabledFilters>
>;

export type BonusReportFiltersValidationSchema = RequiredStrict<
  ChipBasedValidationSchema<typeof bonusReportEnabledFilters>
>;

export interface IFiltersInitialValues extends Record<
  Partial<CHIP_FILTERS_ENTITY>,
  null | IInitialFilter
> {
  [CHIP_FILTERS_ENTITY.FILTER_TYPE]: null;
}

export type BasicFiltersConfigMap = Record<
  FiltersEntityWithoutType,
  Omit<IFiltersConfig, 'order'>
>;

export interface IFilterItem {
  label: string;
  value: string;
  formElement: string;
}

export type FiltersEntityWithoutType = Exclude<
  CHIP_FILTERS_ENTITY,
  CHIP_FILTERS_ENTITY.FILTER_TYPE
>;
